import { useCallback } from 'react';

import { useMutation } from '@apollo/client/react';

import { ENHANCE_IMAGE } from '@modules/_shared/components/image-gallery/enhance/enhance-image-queries';

const JPEG_QUALITY = 0.85;

const imageToJpegBlob = (
  src: string,
  opts: { maxDimension?: number; crossOrigin?: boolean } = {},
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    if (opts.crossOrigin) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => {
      try {
        let { width: targetWidth, height: targetHeight } = img;

        if (opts.maxDimension) {
          const longest = Math.max(targetWidth, targetHeight);
          if (longest > opts.maxDimension) {
            const scale = opts.maxDimension / longest;
            targetWidth = Math.round(targetWidth * scale);
            targetHeight = Math.round(targetHeight * scale);
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        canvas.toBlob(
          blob => {
            blob
              ? resolve(blob)
              : reject(new Error('Failed to convert to JPEG'));
          },
          'image/jpeg',
          JPEG_QUALITY,
        );
      } catch (err) {
        reject(
          err instanceof Error ? err : new Error('Failed to process image'),
        );
      }
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
};

export const resizeImageToBase64 = async (
  src: string,
  maxDimension?: number,
): Promise<string> => {
  const blob = await imageToJpegBlob(src, {
    maxDimension,
    crossOrigin: true,
  });
  const bytes = new Uint8Array(await blob.arrayBuffer());
  return btoa(Array.from(bytes, b => String.fromCharCode(b)).join(''));
};

export const base64ToBlobUrl = async (base64: string): Promise<string> => {
  const dataUrl = `data:image/png;base64,${base64}`;
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

export const base64PngToJpegFile = async (
  base64: string,
  filename = 'enhanced.jpg',
): Promise<File> => {
  const blob = await imageToJpegBlob(`data:image/png;base64,${base64}`);
  return new File([blob], filename, { type: 'image/jpeg' });
};

export type UseEnhanceImageOptions = {
  aspectRatio?: string;
  /** Gemini image output size; omit to use server default (2K). */
  imageSize?: '1K' | '2K' | '4K';
};

export const useEnhanceImage = (opts: UseEnhanceImageOptions = {}) => {
  const [enhanceImageMutation] = useMutation(ENHANCE_IMAGE);

  const { aspectRatio, imageSize } = opts;

  const enhanceImage = useCallback(
    async (prompt: string, sourceImageData: string) => {
      const { data } = await enhanceImageMutation({
        variables: {
          prompt,
          source_image_data: sourceImageData,
          ...(aspectRatio && { aspect_ratio: aspectRatio }),
          ...(imageSize && { image_size: imageSize }),
        },
      });
      const imageData = data?.enhance_image?.image_data;
      if (!imageData) {
        throw new Error('Enhancement failed');
      }
      return { image_data: imageData };
    },
    [enhanceImageMutation, aspectRatio, imageSize],
  );

  return enhanceImage;
};
